import styled, { DefaultTheme } from 'styled-components';
import { Card, Button, Progress, Statistic, Typography, Space, Row, Col, Badge, Select, Skeleton } from 'antd';

// Declare module augmentation for styled-components theme
declare module 'styled-components' {
  export interface DefaultTheme {
    mode?: 'light' | 'dark';
  }
}

// Theme helper function
const getThemeValue = <T,>(props: { theme?: DefaultTheme }, darkValue: T, lightValue: T): T => {
  return props.theme?.mode === 'dark' ? darkValue : lightValue;
};

export const Container = styled.div`
  padding: 16px;
  background: ${(props) => getThemeValue(props, '#171923', 'transparent')};
  color: ${(props) => getThemeValue(props, '#fff', 'inherit')};
  
  @media (min-width: 768px) {
    padding: 20px;
  }
`;

export const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
  
  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

export const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

export const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  
  @media (max-width: 767px) {
    width: 100%;
    justify-content: flex-start;
  }
`;

export const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const MainTitle = styled(Typography.Title)`
  margin: 0 !important;
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
  font-weight: 700 !important;
  
  @media (max-width: 767px) {
    font-size: 20px !important;
  }
`;

export const Subtitle = styled(Typography.Text)`
  color: ${(props) => getThemeValue(props, '#a0aec0', '#666')} !important;
  font-size: 14px;
`;

export const SummaryCard = styled(Card)`
  border-radius: 16px;
  border: 1px solid ${(props) => getThemeValue(props, '#4a5568', '#e5e7eb')};
  margin-bottom: 24px;
  background: ${(props) => getThemeValue(props, '#1a202c', '#f9fafb')};
  box-shadow: ${(props) =>
    getThemeValue(
      props,
      '0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)',
      '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
    )};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;

  .ant-card-body {
    padding: 20px;
    background: ${(props) => getThemeValue(props, '#1a202c', '#f9fafb')};
    
    @media (min-width: 768px) {
      padding: 24px 28px;
    }
    
    @media (min-width: 1024px) {
      padding: 28px 32px;
    }
  }
`;

export const SummaryHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
  
  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

export const SummaryTitle = styled(Typography.Title)`
  margin: 0 !important;
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
  font-weight: 700 !important;
  font-size: 18px !important;
  
  @media (min-width: 768px) {
    font-size: 20px !important;
  }
  
  @media (min-width: 1024px) {
    font-size: 22px !important;
  }
`;

export const SummaryInfo = styled(Space)`
  color: ${(props) => getThemeValue(props, '#a0aec0', '#666')};
  flex-wrap: wrap;
  
  @media (max-width: 767px) {
    width: 100%;
    margin-top: 8px;
  }
`;

export const PeriodText = styled(Typography.Text)`
  color: ${(props) => getThemeValue(props, '#a0aec0', '#666')} !important;
  font-size: 14px;
  display: block;
  margin-bottom: 16px;
`;

export const GrossSalesDisplay = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
  
  @media (min-width: 640px) {
    flex-direction: row;
    align-items: center;
    gap: 16px;
  }
`;

export const GrossSalesAmount = styled(Typography.Title)`
  margin: 0 !important;
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
  font-weight: 700 !important;
  font-size: 28px !important;
  line-height: 1.2;
  
  @media (min-width: 640px) {
    font-size: 36px !important;
  }
  
  @media (min-width: 768px) {
    font-size: 42px !important;
  }
  
  @media (min-width: 1024px) {
    font-size: 48px !important;
  }
`;

export const GrossSalesLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #10b981;
  font-weight: 600;
  font-size: 14px;
  
  @media (min-width: 640px) {
    font-size: 16px;
  }
  
  svg {
    font-size: 16px;
    
    @media (min-width: 640px) {
      font-size: 18px;
    }
  }
`;

export const MetricsGrid = styled(Row)`
  gap: 16px;
  
  .ant-col {
    margin-bottom: 16px;
  }
  
  @media (max-width: 639px) {
    gap: 12px;
    
    .ant-col {
      margin-bottom: 12px;
    }
  }
`;

export const MetricCard = styled(Card)`
  border-radius: 12px;
  border: 1px solid ${(props) => getThemeValue(props, '#4a5568', '#e5e7eb')};
  background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
  box-shadow: ${(props) =>
    getThemeValue(
      props,
      '0 2px 4px rgba(0, 0, 0, 0.2)',
      '0 1px 3px rgba(0, 0, 0, 0.08)'
    )};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  height: 100%;
  cursor: pointer;

  &:hover {
    transform: translateY(-1px);
    border-color: ${(props) => getThemeValue(props, '#718096', '#d1d5db')};
    box-shadow: ${(props) =>
      getThemeValue(
        props,
        '0 8px 16px rgba(0, 0, 0, 0.4)',
        '0 4px 12px rgba(0, 0, 0, 0.15)'
      )};
  }

  .ant-card-body {
    padding: 16px;
    border-radius: 12px;
    background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
    
    @media (min-width: 768px) {
      padding: 20px;
    }
  }
`;

export const MetricHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
`;

export const MetricIcon = styled.div<{ color: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: ${(props) => `${props.color}15`};
  color: ${(props) => props.color};
  flex-shrink: 0;
  font-size: 18px;
  transition: all 0.3s ease;
  
  @media (min-width: 768px) {
    width: 40px;
    height: 40px;
    font-size: 20px;
  }
`;

export const MetricTitle = styled(Typography.Text)`
  font-weight: 600;
  font-size: 13px;
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
  line-height: 1.4;
  
  @media (min-width: 768px) {
    font-size: 14px;
  }
`;

export const MetricValue = styled(Typography.Title)`
  margin: 0 !important;
  font-weight: 700 !important;
  font-size: 22px !important;
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
  line-height: 1.2;
  word-break: break-word;
  
  @media (min-width: 640px) {
    font-size: 24px !important;
  }
  
  @media (min-width: 768px) {
    font-size: 26px !important;
  }
  
  @media (min-width: 1024px) {
    font-size: 28px !important;
  }
`;

export const StyledCard = styled(Card)`
  border-radius: 16px;
  border: 1px solid ${(props) => getThemeValue(props, '#4a5568', '#e5e7eb')};
  margin-bottom: 24px;
  background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
  box-shadow: ${(props) =>
    getThemeValue(
      props,
      '0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)',
      '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
    )};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;

  &:hover {
    box-shadow: ${(props) =>
      getThemeValue(
        props,
        '0 10px 15px -3px rgba(0, 0, 0, 0.4), 0 4px 6px -2px rgba(0, 0, 0, 0.3)',
        '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
      )};
  }

  .ant-card-head {
    border-bottom: 1px solid ${(props) => getThemeValue(props, '#4a5568', '#e5e7eb')};
    background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
    padding: 16px 20px;
    
    @media (min-width: 768px) {
      padding: 20px 24px;
    }
  }

  .ant-card-head-title {
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
    font-weight: 600;
    font-size: 16px;
    
    @media (min-width: 768px) {
      font-size: 18px;
    }
  }

  .ant-card-body {
    padding: 16px;
    background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
    
    @media (min-width: 768px) {
      padding: 20px 24px;
    }
  }
`;

export const WeeklyBreakdownItem = styled.div`
  margin-bottom: 20px;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  @media (max-width: 767px) {
    margin-bottom: 16px;
  }
`;

export const WeeklyBreakdownHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  flex-wrap: wrap;
  gap: 8px;
  
  @media (max-width: 639px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
`;

export const WeeklyBreakdownWeek = styled(Typography.Text)`
  font-weight: 500;
  font-size: 13px;
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
  
  @media (min-width: 768px) {
    font-size: 14px;
  }
`;

export const WeeklyBreakdownAmount = styled(Typography.Text)`
  font-weight: 700;
  font-size: 13px;
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
  
  @media (min-width: 768px) {
    font-size: 14px;
  }
`;

export const StyledProgress = styled(Progress)`
  .ant-progress-bg {
    background: linear-gradient(90deg, #10b981 0%, #059669 100%);
  }
`;

export const StatsGrid = styled(Row)`
  gap: 16px;
  
  .ant-col {
    margin-bottom: 16px;
  }
  
  @media (max-width: 639px) {
    gap: 12px;
    
    .ant-col {
      margin-bottom: 12px;
    }
  }
`;

export const StatCard = styled(Card)`
  border-radius: 12px;
  border: 1px solid ${(props) => getThemeValue(props, '#4a5568', '#e5e7eb')};
  background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
  box-shadow: ${(props) =>
    getThemeValue(
      props,
      '0 2px 4px rgba(0, 0, 0, 0.2)',
      '0 1px 3px rgba(0, 0, 0, 0.08)'
    )};
  height: 100%;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${(props) =>
      getThemeValue(
        props,
        '0 4px 8px rgba(0, 0, 0, 0.3)',
        '0 2px 6px rgba(0, 0, 0, 0.1)'
      )};
  }

  .ant-card-body {
    padding: 16px;
    border-radius: 12px;
    background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
    
    @media (min-width: 768px) {
      padding: 20px;
    }
  }
`;

export const StyledStatistic = styled(Statistic)`
  .ant-statistic-title {
    color: ${(props) => getThemeValue(props, '#a0aec0', '#666')} !important;
    font-size: 12px;
    font-weight: 500;
    margin-bottom: 8px;
    
    @media (min-width: 768px) {
      font-size: 13px;
    }
  }

  .ant-statistic-content {
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
    font-size: 20px;
    font-weight: 700;
    line-height: 1.2;
    
    @media (min-width: 640px) {
      font-size: 22px;
    }
    
    @media (min-width: 768px) {
      font-size: 24px;
    }
  }
`;

export const EmptyStateText = styled(Typography.Text)`
  color: ${(props) => getThemeValue(props, '#a0aec0', '#666')} !important;
  text-align: center;
  display: block;
  padding: 24px 16px;
  font-size: 14px;
  
  @media (min-width: 768px) {
    padding: 32px 0;
    font-size: 15px;
  }
`;

export const BackButton = styled(Button)`
  border-radius: 12px;
  width: 46px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: #2D6CCA;
  color: #ffffff;
  box-shadow: ${(props) =>
    getThemeValue(
      props,
      '0 2px 4px rgba(0, 0, 0, 0.3)',
      '0 2px 8px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08)'
    )};
  transition: all 0.2s ease;
  flex-shrink: 0;
  padding: 0;

  &:hover {
    background: #1E5AB5;
    box-shadow: ${(props) =>
      getThemeValue(
        props,
        '0 4px 6px rgba(0, 0, 0, 0.4)',
        '0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.1)'
      )};
  }
  
  .anticon {
    font-size: 16px;
  }
`;

export const FilterSection = styled(Space)`
  width: 100%;
  
  @media (max-width: 639px) {
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
  }
`;

export const FilterLabel = styled(Typography.Text)`
  color: ${(props) => getThemeValue(props, '#a0aec0', '#666')} !important;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
`;

export const StyledSelect = styled(Select)`
  min-width: 150px;
  width: 100%;
  
  .ant-select-selector {
    background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')} !important;
    border: 1px solid ${(props) => getThemeValue(props, '#4a5568', '#2D6CCA')} !important;
    border-radius: 8px !important;
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
    padding: 4px 11px !important;
    height: auto !important;
    min-height: 32px !important;
    transition: all 0.2s ease !important;
    
    &:hover {
      border-color: ${(props) => getThemeValue(props, '#718096', '#1E5AB5')} !important;
    }
  }
  
  .ant-select-selection-item {
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
    font-size: 14px !important;
    line-height: 22px !important;
    padding: 0 !important;
  }
  
  .ant-select-selection-placeholder {
    color: ${(props) => getThemeValue(props, '#a0aec0', '#8c8c8c')} !important;
    font-size: 14px !important;
    line-height: 22px !important;
  }
  
  .ant-select-arrow {
    color: ${(props) => getThemeValue(props, '#a0aec0', '#8c8c8c')} !important;
    font-size: 12px !important;
  }
  
  &.ant-select-focused .ant-select-selector {
    border-color: ${(props) => getThemeValue(props, '#2D6CCA', '#2D6CCA')} !important;
    box-shadow: ${(props) =>
      getThemeValue(
        props,
        '0 0 0 2px rgba(45, 108, 202, 0.2)',
        '0 0 0 2px rgba(45, 108, 202, 0.1)'
      )} !important;
  }
  
  &:hover .ant-select-selector {
    border-color: ${(props) => getThemeValue(props, '#718096', '#1E5AB5')} !important;
  }
  
  &.ant-select-disabled .ant-select-selector {
    background: ${(props) => getThemeValue(props, '#2d3748', '#f5f5f5')} !important;
    border-color: ${(props) => getThemeValue(props, '#4a5568', '#d9d9d9')} !important;
    cursor: not-allowed !important;
  }
  
  @media (min-width: 768px) {
    min-width: 180px;
  }
`;

export const StyledSkeletonSmall = styled(Skeleton.Input)`
  width: 150px;
`;

export const StyledSkeletonMedium = styled(Skeleton.Input)`
  width: 300px;
  margin-bottom: 16px;
`;

export const StyledSkeletonLarge = styled(Skeleton.Input)`
  width: 200px;
  height: 48px;
`;

export const CardTitle = styled(Typography.Title)`
  margin: 0 !important;
`;

export const KeyMetricsTitle = styled(Typography.Title)`
  margin-bottom: 16px !important;
  font-weight: 700 !important;
`;

export const StatisticSubtext = styled(Typography.Text)`
  font-size: 12px;
  display: block;
  margin-top: 4px;
`;
